import cx from "classnames";
import BackgroundImage from "gatsby-background-image";
import { type GatsbyImageProps, getImage } from "gatsby-plugin-image";
import { convertToBgImage } from "gbimage-bridge";

import React, { type FC, type HTMLAttributes } from "react";

import { IconSprite } from "../IconSprite";
import { Link } from "../Link";
import { TagsList } from "../TagsList";
import type { TagData } from "../TagsList/TagsList";
import { TogglableContent } from "../TogglableContent";
import * as styles from "./SignpostCard.module.scss";

interface Props extends HTMLAttributes<HTMLElement> {
	heading: string;
	url?: string;
	image?: GatsbyImageProps | null;
	variant?: "vertical" | "horizontal";
	backgroundColor?: "white" | "grey";
	linkText?: string;
	summary?: string;
	tags?: Array<TagData | null> | null;
	mobileToggleTagsString?: string;
}

// eslint-disable-next-line complexity
export const SignpostCard: FC<Props> = ({
	heading,
	url = "",
	image,
	className,
	linkText,
	variant = "vertical",
	backgroundColor = "grey",
	children,
	tags,
	summary,
	mobileToggleTagsString,
	...props
}) => {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	const bgImage = convertToBgImage(getImage(image as any));
	return (
		<div
			className={cx(
				styles.component,
				className,
				styles[`${backgroundColor}Bg`],
				styles[image ? "withImage" : "noImage"],
				styles[variant],
			)}
			{...props}
		>
			<Link to={url} className={styles.mainLink}>
				{heading}
			</Link>
			{image && (
				<figure className={styles.imageWrapper}>
					<BackgroundImage
						className={styles.image}
						fluid={bgImage.fluid}
						alt={heading}
					/>
				</figure>
			)}
			<div className={styles.contentArea}>
				<div className={styles.mainArea}>
					<div>
						<h2>
							<span className={styles.title}>{heading}</span>
						</h2>

						{summary && <p>{summary}</p>}
						{children}
					</div>

					<div
						className={cx(styles.tagsWrapper, {
							[styles.hasTags]: !!tags?.length,
							[styles.desktopOnly]: mobileToggleTagsString,
						})}
					>
						{!!tags?.length && <TagsList tags={tags} variant="horizontal" />}
					</div>

					{!!tags?.length && mobileToggleTagsString && (
						<TogglableContent
							className={styles.mobileOnly}
							contentName={mobileToggleTagsString}
						>
							<div className={styles.togglableTags}>
								<hr />
								<TagsList tags={tags} variant="horizontal" />
							</div>
						</TogglableContent>
					)}
				</div>

				<div className={styles.rightCol}>
					{linkText && (
						<Link to={url} className={styles.cta}>
							<span>{linkText}</span>
							<IconSprite name="chevron-right" />
						</Link>
					)}
				</div>
			</div>
		</div>
	);
};

export default SignpostCard;
